import { useState, useEffect, useContext } from "react";
import axios from "axios";
import Preloader from "../../../shared/PreLoaders";
import { Authorization } from "../../../Context/Authorization";
import { AuthContext } from "../../../Context/AuthContext";
import TableComponent from "../../../utils/TableComponent";

const inputStyle = {
  width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0",
  borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff",
  outline: "none", transition: "border-color 0.2s", cursor: "pointer",
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "flex", alignItems: "center", gap: 5 };

const VFCBasicReports = () => {
  const Api = process.env.REACT_APP_API;
  const { selectedJfmcType } = useContext(AuthContext);

  const [loading, setLoading]               = useState(false);
  const [circle, setCircle]                 = useState([]);
  const [division, setDivision]             = useState([]);
  const [ranges, setRanges]                 = useState([]);
  const [vfcOptions, setVfcOptions]         = useState([]);
  const [selectedCircle, setSelectedCircle] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("");
  const [selectedRange, setSelectedRange]   = useState("");
  const [selectedVfc, setSelectedVfc]       = useState("");
  const [viewData, setViewData]             = useState(false);
  const [reportData, setReportData]         = useState(null);
  const [showCircle, setShowCircle]         = useState(false);
  const [showDivision, setShowDivision]     = useState(false);
  const [showRange, setShowRange]           = useState(false);
  const [showVfc, setShowVfc]               = useState(false);
  const [dateType, setDateType]             = useState("specific");
  const [specificDate, setSpecificDate]     = useState("");
  const [fromDate, setFromDate]             = useState("");
  const [toDate, setToDate]                 = useState("");

  useEffect(() => {
    const fetchMasterData = async () => {
      setLoading(true);
      try {
        const config = Authorization();
        const [circleRes, divisionRes, rangeRes] = await Promise.all([
          axios.get(`${Api}/api/circles`, config),
          axios.get(`${Api}/api/divisions`, config),
          axios.get(`${Api}/api/ranges`, config),
        ]);
        setCircle(circleRes.data || []);
        setDivision(divisionRes.data || []);
        setRanges(rangeRes.data || []);
      } catch (err) {
        console.error("Error loading master data", err);
      }
      setLoading(false);
    };
    fetchMasterData();
  }, [Api]);

  useEffect(() => {
    const fetchVfcs = async () => {
      if (!selectedJfmcType) return;
      setLoading(true);
      try {
        const config = Authorization();
        const res = await axios.post(`${Api}/api/vfcs/names`, { jfmcTypeId: selectedJfmcType._id }, config);
        setVfcOptions(res.data.vfcs);
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    fetchVfcs();
  }, [selectedJfmcType, Api]);

  const handleViewClick = async (e) => {
    e.preventDefault();
    if (
      (dateType === "specific" && !specificDate) ||
      (dateType === "range" && (!fromDate || !toDate))
    ) {
      alert("Please select date");
      return;
    }
    setViewData(false);
    setLoading(true);
    const datePayload = dateType === "specific"
      ? { type: "specific", value: specificDate }
      : { type: "range", from: fromDate, to: toDate };
    try {
      const config = Authorization();
      const res = await axios.post(
        `${Api}/api/vfcs/vfc-daily-report`,
        {
          date: datePayload,
          jfmcTypeId: selectedJfmcType?._id || null,
          circleIds:   selectedCircle   ? [selectedCircle]   : [],
          divisionIds: selectedDivision ? [selectedDivision] : [],
          rangeIds:    selectedRange    ? [selectedRange]    : [],
          vfcIds:      selectedVfc      ? [selectedVfc]      : [],
        },
        config
      );
      setReportData(res.data);
      setViewData(true);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const tableData = reportData?.data?.map((vfc) => {
    const ecDates  = vfc.meetings?.EC?.dates      || [];
    const genDates = vfc.meetings?.GENERAL?.dates || [];
    return {
      vfcName:              vfc.vfcName,
      loanDisbursedAmount:  vfc.loanDisbursedAmount,
      repaymentAmount:      vfc.repaymentAmount,
      interestCollected:    vfc.interestCollected,
      receiptAmount:        vfc.receiptAmount,
      paymentAmount:        vfc.paymentAmount,
      ecMeetings:           vfc.meetings?.EC?.count      || 0,
      generalMeetings:      vfc.meetings?.GENERAL?.count || 0,
      meetingDates:         [...ecDates, ...genDates].join(", "),
      documents: [
        ...(vfc.documents?.loan    || []),
        ...(vfc.documents?.meeting || []),
        ...(vfc.documents?.receipt || []),
        ...(vfc.documents?.payment || []),
      ],
    };
  });

  const column = [
    { headerName: "VFC Name",           field: "vfcName",             pinned: "left", minWidth: 180 },
    { headerName: "Loan Disbursed (₹)", field: "loanDisbursedAmount", type: "numericColumn" },
    { headerName: "Repayment (₹)",      field: "repaymentAmount",     type: "numericColumn" },
    { headerName: "Interest (₹)",       field: "interestCollected",   type: "numericColumn" },
    { headerName: "Receipt (₹)",        field: "receiptAmount",       type: "numericColumn" },
    { headerName: "Payment (₹)",        field: "paymentAmount",       type: "numericColumn" },
    { headerName: "EC Meetings",        field: "ecMeetings" },
    { headerName: "General Meetings",   field: "generalMeetings" },
    { headerName: "Meeting Dates",      field: "meetingDates",        minWidth: 220 },
    {
      headerName: "Documents",
      field: "documents",
      cellRenderer: (params) => {
        if (!params.value || params.value.length === 0) return "—";
        return params.value.map((url, i) => (
          <a key={i} href={url} target="_blank" rel="noopener noreferrer" style={{ marginRight: 8 }}>View</a>
        ));
      },
      minWidth: 200,
    },
  ];

  /* ── Filter toggle pill ── */
  const TogglePill = ({ label, icon, checked, onChange }) => (
    <label style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 20, border: `1.5px solid ${checked ? "#2E7D32" : "#E2E8F0"}`, background: checked ? "#E8F5E9" : "#fff", color: checked ? "#1B5E20" : "#718096", fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.2s", userSelect: "none" }}>
      <input type="checkbox" checked={checked} onChange={onChange} style={{ display: "none" }} />
      <i className={`mdi ${icon}`} style={{ fontSize: 14 }} />
      {label}
    </label>
  );

  /* ════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════ */
  return (
    <div>
      {loading && <Preloader loading={loading} />}

      {/* ── Page Header ── */}
      <div style={{ marginBottom: 20 }}>
        <h4 style={{ margin: 0, fontWeight: 700, color: "#1A202C", fontSize: 18 }}>JFMC Daily Report</h4>
        <div style={{ fontSize: 12, color: "#718096", marginTop: 3 }}>
          <span style={{ color: "#A0AEC0" }}>JFMC</span>
          <span style={{ margin: "0 6px", color: "#CBD5E0" }}>›</span>
          <span style={{ color: "#2E7D32", fontWeight: 600 }}>Daily Report</span>
        </div>
      </div>

      {/* ── No JFMC type selected ── */}
      {!selectedJfmcType ? (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #FED7D7", padding: "48px 24px", textAlign: "center" }}>
          <div style={{ width: 60, height: 60, background: "#FFF5F5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
            <i className="mdi mdi-tag-outline" style={{ fontSize: 28, color: "#E53E3E" }} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>JFMC Type not selected</div>
          <div style={{ fontSize: 12, color: "#A0AEC0" }}>Use the <strong>JFMC Type</strong> selector in the top navigation bar</div>
        </div>
      ) : (
        <>
          {/* ── Filter Card ── */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", padding: "20px 24px", marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <span style={{ width: 30, height: 30, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14 }}>
                <i className="mdi mdi-filter-variant" />
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>Filter Report</span>
              <span style={{ fontSize: 11, color: "#A0AEC0", marginLeft: 4 }}>Toggle filters and select a date to view records</span>
            </div>

            {/* Toggle pills */}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
              <TogglePill label="Circle" icon="mdi-circle-outline" checked={showCircle} onChange={(e) => { setShowCircle(e.target.checked); if (!e.target.checked) setSelectedCircle(""); }} />
              <TogglePill label="Division" icon="mdi-domain" checked={showDivision} onChange={(e) => { setShowDivision(e.target.checked); if (!e.target.checked) setSelectedDivision(""); }} />
              <TogglePill label="Range" icon="mdi-map-marker-outline" checked={showRange} onChange={(e) => { setShowRange(e.target.checked); if (!e.target.checked) setSelectedRange(""); }} />
              <TogglePill label="VFC" icon="mdi-home-group" checked={showVfc} onChange={(e) => { setShowVfc(e.target.checked); if (!e.target.checked) setSelectedVfc(""); }} />
            </div>

            {/* Conditional selects */}
            {(showCircle || showDivision || showRange || showVfc) && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 12, marginBottom: 16 }}>
                {showCircle && (
                  <div>
                    <label style={labelStyle}><i className="mdi mdi-circle-outline" style={{ color: "#43A047" }} /> Circle</label>
                    <select value={selectedCircle} onChange={(e) => setSelectedCircle(e.target.value)} style={inputStyle} onFocus={(e) => e.target.style.borderColor = "#4CAF50"} onBlur={(e) => e.target.style.borderColor = "#E2E8F0"}>
                      <option value="">— Select —</option>
                      {circle.map((c) => <option key={c._id} value={c._id}>{c.circleName}</option>)}
                    </select>
                  </div>
                )}
                {showDivision && (
                  <div>
                    <label style={labelStyle}><i className="mdi mdi-domain" style={{ color: "#43A047" }} /> Division</label>
                    <select value={selectedDivision} onChange={(e) => setSelectedDivision(e.target.value)} style={inputStyle} onFocus={(e) => e.target.style.borderColor = "#4CAF50"} onBlur={(e) => e.target.style.borderColor = "#E2E8F0"}>
                      <option value="">— Select —</option>
                      {division.map((d) => <option key={d._id} value={d._id}>{d.divisionName}</option>)}
                    </select>
                  </div>
                )}
                {showRange && (
                  <div>
                    <label style={labelStyle}><i className="mdi mdi-map-marker-outline" style={{ color: "#43A047" }} /> Range</label>
                    <select value={selectedRange} onChange={(e) => setSelectedRange(e.target.value)} style={inputStyle} onFocus={(e) => e.target.style.borderColor = "#4CAF50"} onBlur={(e) => e.target.style.borderColor = "#E2E8F0"}>
                      <option value="">— Select —</option>
                      {ranges.map((r) => <option key={r._id} value={r._id}>{r.rangeName}</option>)}
                    </select>
                  </div>
                )}
                {showVfc && (
                  <div>
                    <label style={labelStyle}><i className="mdi mdi-home-group" style={{ color: "#43A047" }} /> VFC</label>
                    <select value={selectedVfc} onChange={(e) => setSelectedVfc(e.target.value)} style={inputStyle} onFocus={(e) => e.target.style.borderColor = "#4CAF50"} onBlur={(e) => e.target.style.borderColor = "#E2E8F0"}>
                      <option value="">— Select —</option>
                      {vfcOptions.map((v) => <option key={v._id} value={v._id}>{v.vfcName}</option>)}
                    </select>
                  </div>
                )}
              </div>
            )}

            {/* Date filter row */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 12, alignItems: "end" }}>
              <div>
                <label style={labelStyle}><i className="mdi mdi-calendar-range" style={{ color: "#43A047" }} /> Date Type</label>
                <select value={dateType} onChange={(e) => setDateType(e.target.value)} style={inputStyle} onFocus={(e) => e.target.style.borderColor = "#4CAF50"} onBlur={(e) => e.target.style.borderColor = "#E2E8F0"}>
                  <option value="specific">Specific Date</option>
                  <option value="range">From — To</option>
                </select>
              </div>

              {dateType === "specific" && (
                <div>
                  <label style={labelStyle}><i className="mdi mdi-calendar" style={{ color: "#43A047" }} /> Select Date</label>
                  <input type="date" value={specificDate} onChange={(e) => setSpecificDate(e.target.value)} style={inputStyle} onFocus={(e) => e.target.style.borderColor = "#4CAF50"} onBlur={(e) => e.target.style.borderColor = "#E2E8F0"} />
                </div>
              )}

              {dateType === "range" && (
                <>
                  <div>
                    <label style={labelStyle}><i className="mdi mdi-calendar-start" style={{ color: "#43A047" }} /> From Date</label>
                    <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={inputStyle} onFocus={(e) => e.target.style.borderColor = "#4CAF50"} onBlur={(e) => e.target.style.borderColor = "#E2E8F0"} />
                  </div>
                  <div>
                    <label style={labelStyle}><i className="mdi mdi-calendar-end" style={{ color: "#43A047" }} /> To Date</label>
                    <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={inputStyle} onFocus={(e) => e.target.style.borderColor = "#4CAF50"} onBlur={(e) => e.target.style.borderColor = "#E2E8F0"} />
                  </div>
                </>
              )}

              <div style={{ display: "flex", alignItems: "flex-end" }}>
                <button
                  onClick={handleViewClick}
                  style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 20px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 3px 10px rgba(46,125,50,0.3)", whiteSpace: "nowrap" }}
                >
                  <i className="mdi mdi-magnify" /> View Report
                </button>
              </div>
            </div>
          </div>

          {/* ── Results Table Card ── */}
          {viewData && tableData && (
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
                <span style={{ width: 30, height: 30, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15 }}>
                  <i className="mdi mdi-table-large" />
                </span>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#fff", flex: 1 }}>Report Results</span>
                <span style={{ background: "rgba(255,255,255,0.2)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 50 }}>{tableData.length} records</span>
              </div>
              <div style={{ padding: "12px 16px" }}>
                <TableComponent data={tableData} columns={column} />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VFCBasicReports;
